import { z } from 'zod';

/**
 * Admin Stripe settings (ADR-013) — lets an admin configure Stripe from the
 * dashboard instead of editing `.env` on the server. Secrets
 * (secretKey/webhookSecret/connectWebhookSecret) are AES-256-GCM encrypted
 * at rest and are NEVER returned in full by any response here — masked
 * only (e.g. "sk_live_••••4242"). A database value overrides the matching
 * `.env` var; omitting a field from the PATCH body leaves it untouched,
 * and sending an explicit `null` clears the override so that field falls
 * back to `.env` again — see ADR-013 §1.
 */

export const SettingsSourceSchema = z.enum(['database', 'env']);
export type SettingsSource = z.infer<typeof SettingsSourceSchema>;

export const SecretSourceSchema = z.enum(['database', 'env', 'none']);
export type SecretSource = z.infer<typeof SecretSourceSchema>;

export const MaskedSecretSchema = z.object({
  configured: z.boolean(),
  source: SecretSourceSchema,
  /** e.g. "sk_live_••••4242" — never the real value. Null when not configured anywhere. */
  masked: z.string().nullable(),
});
export type MaskedSecret = z.infer<typeof MaskedSecretSchema>;

export const StripeSettingsAdminViewSchema = z.object({
  secretKey: MaskedSecretSchema.extend({ mode: z.enum(['live', 'test']).nullable() }),
  webhookSecret: MaskedSecretSchema,
  connectWebhookSecret: MaskedSecretSchema,
  platformFeePercent: z.object({ value: z.number().min(0).max(1), source: SettingsSourceSchema }),
  currency: z.object({ value: z.string(), source: SettingsSourceSchema }),
  statementDescriptor: z.object({ value: z.string().nullable(), source: SettingsSourceSchema }),
  connectReturnUrl: z.object({ value: z.string(), source: SettingsSourceSchema }),
  connectRefreshUrl: z.object({ value: z.string(), source: SettingsSourceSchema }),
  /** Advisory only — no in-app job scheduler exists (ADR-009 §8); this just tells the operator what to point their external cron at. */
  payoutSchedule: z.object({
    weekday: z.number().int().min(0).max(6).nullable(),
    hour: z.number().int().min(0).max(23).nullable(),
  }),
  /** DB-derived only — never a live Stripe API call. Stamped when a webhook's signature verifies, regardless of event type. */
  webhookHealth: z.object({
    platform: z.object({ lastReceivedAt: z.string().datetime().nullable() }),
    connect: z.object({ lastReceivedAt: z.string().datetime().nullable() }),
  }),
  updatedAt: z.string().datetime().nullable(),
  updatedByUserId: z.string().nullable(),
});
export type StripeSettingsAdminView = z.infer<typeof StripeSettingsAdminViewSchema>;

/** Every field: omit entirely = leave untouched; explicit `null` = clear the DB override (revert to .env). */
export const UpdateStripeSettingsRequestSchema = z.object({
  secretKey: z
    .string()
    .regex(/^(sk|rk)_(live|test)_/, 'Must be a Stripe secret/restricted key (sk_live_/sk_test_/rk_live_/rk_test_)')
    .nullable()
    .optional(),
  webhookSecret: z.string().regex(/^whsec_/, 'Must start with whsec_').nullable().optional(),
  connectWebhookSecret: z.string().regex(/^whsec_/, 'Must start with whsec_').nullable().optional(),
  platformFeePercent: z.number().min(0).max(1).nullable().optional(),
  currency: z
    .string()
    .length(3)
    .regex(/^[a-zA-Z]{3}$/, 'Must be a 3-letter ISO currency code')
    .nullable()
    .optional(),
  statementDescriptor: z.string().max(22).nullable().optional(),
  connectReturnUrl: z.string().url().nullable().optional(),
  connectRefreshUrl: z.string().url().nullable().optional(),
  payoutWeekday: z.number().int().min(0).max(6).nullable().optional(),
  payoutHour: z.number().int().min(0).max(23).nullable().optional(),
});
export type UpdateStripeSettingsRequest = z.infer<typeof UpdateStripeSettingsRequestSchema>;
